import { TowerType } from '../entities/Tower';

// ============================================
// Level Stats Tracker
// Tracks tower performance during gameplay
// ============================================

export interface TowerDamageStats {
  towerType: TowerType;
  displayName: string;
  totalDamage: number;
  kills: number;
  towersBuilt: number;
  totalCost: number;
  damagePerGold: number;
}

export interface LevelStats {
  scenarioId: string;
  timestamp: string;
  towersStats: Record<TowerType, TowerDamageStats>;
  totalDamage: number;
  totalKills: number;
  totalTowerCost: number;
  wavesCompleted: number;
  timeElapsed: number;
}

const TOWER_DISPLAY_NAMES: Record<TowerType, string> = {
  arrow: 'Arrow Tower',
  cannon: 'Cannon',
  antiair: 'Anti-Air',
  tesla: 'Tesla Coil',
  laser: 'Laser',
  mortar: 'Mortar',
  ice: 'Frost Keep',
  fire: 'Flame Thrower',
  hive: 'Bee Hive',
  gem: 'Gem Tower',
  trap: 'Trap',
  lightning: 'Lightning',
};

class LevelStatsTrackerManager {
  private currentStats: LevelStats | null = null;
  private scenarioId: string = '';

  /**
   * Start tracking stats for a new level
   */
  startLevel(scenarioId: string): void {
    this.scenarioId = scenarioId;
    this.currentStats = {
      scenarioId,
      timestamp: new Date().toISOString(),
      towersStats: this.createEmptyTowerStats(),
      totalDamage: 0,
      totalKills: 0,
      totalTowerCost: 0,
      wavesCompleted: 0,
      timeElapsed: 0,
    };
    console.log(`[LevelStatsTracker] Started tracking for ${scenarioId}`);
  }

  private createEmptyTowerStats(): Record<TowerType, TowerDamageStats> {
    const stats: Partial<Record<TowerType, TowerDamageStats>> = {};
    const towerTypes: TowerType[] = [
      'arrow', 'cannon', 'antiair', 'tesla', 'laser', 'mortar',
      'ice', 'fire', 'hive', 'gem', 'trap', 'lightning'
    ];

    for (const type of towerTypes) {
      stats[type] = {
        towerType: type,
        displayName: TOWER_DISPLAY_NAMES[type],
        totalDamage: 0,
        kills: 0,
        towersBuilt: 0,
        totalCost: 0,
        damagePerGold: 0,
      };
    }

    return stats as Record<TowerType, TowerDamageStats>;
  }

  /**
   * Record damage dealt by a tower
   */
  recordDamage(towerType: TowerType, damage: number, killed: boolean): void {
    if (!this.currentStats) return;

    const stats = this.currentStats.towersStats[towerType];
    if (stats) {
      stats.totalDamage += damage;
      if (killed) {
        stats.kills++;
        this.currentStats.totalKills++;
      }
      this.currentStats.totalDamage += damage;
      
      // Update damage per gold
      if (stats.totalCost > 0) {
        stats.damagePerGold = stats.totalDamage / stats.totalCost;
      }
    }
  }

  /**
   * Record a tower being built
   */
  recordTowerBuilt(towerType: TowerType, cost: number): void {
    if (!this.currentStats) return;

    const stats = this.currentStats.towersStats[towerType];
    if (stats) {
      stats.towersBuilt++;
      stats.totalCost += cost;
      this.currentStats.totalTowerCost += cost;
      
      // Update damage per gold
      if (stats.totalCost > 0) {
        stats.damagePerGold = stats.totalDamage / stats.totalCost;
      }
    }
  }

  /**
   * Record wave completed
   */
  recordWaveCompleted(): void {
    if (!this.currentStats) return;
    this.currentStats.wavesCompleted++;
  }

  /**
   * Finalize stats at end of level
   */
  finishLevel(timeElapsed: number): LevelStats | null {
    if (!this.currentStats) return null;

    this.currentStats.timeElapsed = timeElapsed;

    // Finalize all damage per gold calculations
    for (const type in this.currentStats.towersStats) {
      const stats = this.currentStats.towersStats[type as TowerType];
      if (stats.totalCost > 0) {
        stats.damagePerGold = Math.round((stats.totalDamage / stats.totalCost) * 100) / 100;
      }
    }

    console.log(`[LevelStatsTracker] Finished level ${this.scenarioId}:`, this.currentStats);
    return { ...this.currentStats };
  }

  /**
   * Get current stats (for live display)
   */
  getCurrentStats(): LevelStats | null {
    return this.currentStats ? { ...this.currentStats } : null;
  }

  /**
   * Get tower stats sorted by damage (descending)
   */
  getSortedTowerStats(): TowerDamageStats[] {
    if (!this.currentStats) return [];

    return Object.values(this.currentStats.towersStats)
      .filter(s => s.totalDamage > 0 || s.towersBuilt > 0)
      .sort((a, b) => b.totalDamage - a.totalDamage);
  }

  /**
   * Reset tracker
   */
  reset(): void {
    this.currentStats = null;
    this.scenarioId = '';
  }
}

// Singleton
export const LevelStatsTracker = new LevelStatsTrackerManager();
